import React, { useCallback, useLayoutEffect, useState } from 'react';
import head from 'lodash/head';
import { useDispatch, useSelector } from 'react-redux';
import { Ellipse } from '@yme/assets/icons';
import { SVGIconStyled } from '@yme/atoms/color-selector/styled';
import { Dropdown } from '@yme/atoms/dropdown';
import { MobileDropdownSlider } from '@yme/atoms/mobile-dropdown-slider';
import { ModalWindowFooter } from '@yme/atoms/modal-footer';
import { MD_MODAL, StaticModalWindow } from '@yme/atoms/modal-window';
import { ICON_SIZE, SVGIcon } from '@yme/atoms/svg-icon';
import { getRequiredProductVariantBySku } from '@yme/components/ProductDetailsPage/components/ProductPart/helpers';
import { useWindowSize } from '@yme/hooks/useWindowSize';
import { getProductCardLabels } from '@yme/pdp-components/ProductCard';
import { getRTL, getRootLang } from '@yme/selectors/common';
import { selectProductVariantsByColor } from '@yme/selectors/helpers';
import { colorCoreBlack300 } from '@yme/tokens/colors';
import { FlexWrapper } from '@yme/tokens/flexTokens';
import { RegularTextLevel4 } from '@yme/tokens/typography';
import { setIsModalHide } from '../actions';
import { getModalData } from '../selectors';
import { RequiredProductVariantPanel } from './RequiredProductVariantPanel';
import { HIDE_MODAL_ANIMATION_TIME } from './constants';
export function RequiredPartVariantModal() {
    const { item, pdpPath, isInAuthor, primaryButtonContent, secondaryButtonContent, labels, callback, currentVariant, } = useSelector(getModalData);
    const productColorVariants = selectProductVariantsByColor({
        variantsData: item.variants,
    });
    const [currentSelectedVariant, setCurrentSelectedVariant] = useState(currentVariant);
    const [selectedDropdownValue, setSelectedDropdownValue] = useState(currentVariant.sku);
    const [isMobileModalActive, setIsMobileModalActive] = useState(false);
    const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] = useState(currentVariant.stock === 0);
    const isRightToLeft = useSelector(getRTL);
    const rootLanguage = useSelector(getRootLang);
    const { isInfiniteDesktopView } = useWindowSize();
    const dispatch = useDispatch();
    const { productCard_controlMethod_label: controlMethod, productCard_startingMethodAndTilt_label: startingMethod, productCard_shaftLength_label: shaftLength, productCard_rotation_label: rotation, productCard_colorSelector_label: colourName, product_size_label: size, } = useSelector(getProductCardLabels);
    const productPropertiesLabels = {
        controlMethod,
        startingMethod,
        shaftLength,
        rotation,
        colourName,
        size,
    };
    const colorDropdownData = productColorVariants.map((variant) => ({
        label: variant.colourName,
        value: head(variant.variantsOfProduct)?.sku || '',
        icon: (React.createElement(SVGIconStyled, { colourCode: variant.colourCode, size: ICON_SIZE.s, isActive: head(variant.variantsOfProduct)?.sku === selectedDropdownValue, isRightToLeft: isRightToLeft, isSelectorBigSize: true, paddingSize: 0 },
            React.createElement(SVGIcon, { type: Ellipse, size: ICON_SIZE.xxs, rectFill: variant.colourCode, title: colourName }))),
    }));
    useLayoutEffect(() => {
        let timeout;
        if (!isInfiniteDesktopView) {
            timeout = setTimeout(() => {
                setIsMobileModalActive(true);
            }, 0);
        }
        return () => clearTimeout(timeout);
    }, [isInfiniteDesktopView]);
    const closeMobileModal = useCallback(() => {
        setIsMobileModalActive(false);
        setTimeout(() => {
            dispatch(setIsModalHide());
        }, HIDE_MODAL_ANIMATION_TIME);
    }, [dispatch]);
    const handleHideModal = useCallback(() => {
        dispatch(setIsModalHide());
    }, [dispatch]);
    const onSubmitHandleModal = () => {
        const [price] = getRequiredProductVariantBySku(item.variants, selectedDropdownValue).prices;
        callback(selectedDropdownValue, price);
        if (isInfiniteDesktopView) {
            dispatch(setIsModalHide());
        }
        else {
            closeMobileModal();
        }
    };
    const onChangeDateDropdown = (value) => {
        setSelectedDropdownValue(value);
        const currentSelectedVariantValue = getRequiredProductVariantBySku(item.variants, value);
        if (currentSelectedVariantValue) {
            const { stock } = currentSelectedVariantValue;
            setIsSubmitButtonDisabled(stock === 0);
            setCurrentSelectedVariant(currentSelectedVariantValue);
        }
    };
    const modalContent = (React.createElement(FlexWrapper, { gap: "24px", flexDirection: "column", isFullWidth: true },
        React.createElement(RequiredProductVariantPanel, { item: item, pdpPath: pdpPath, productPropertiesLabels: productPropertiesLabels, rootLanguage: rootLanguage, isInAuthor: isInAuthor, isRightToLeft: isRightToLeft, currentVariant: currentSelectedVariant }),
        React.createElement(RegularTextLevel4, { color: colorCoreBlack300 }, labels?.partsAccessoriesMerch_addingRequiredPartPopUp_description),
        colorDropdownData.length > 1 && (React.createElement(Dropdown, { id: "id", activeItem: selectedDropdownValue, items: colorDropdownData, onChange: onChangeDateDropdown, inputPlaceholder: labels?.partsAccessoriesMerch_chooseColor_label, labelText: labels?.partsAccessoriesMerch_chooseColor_label, isRightToLeft: isRightToLeft }))));
    return isInfiniteDesktopView ? (React.createElement(StaticModalWindow, { onHide: handleHideModal, minHeight: "auto", staticModalSize: MD_MODAL, title: labels?.partsAccessoriesMerch_requiredPartVariantPopUp_header, isRightToLeft: isRightToLeft, footerComponent: React.createElement(ModalWindowFooter, { isCancelButton: Boolean(secondaryButtonContent), isSubmitButton: true, submitButtonLabel: primaryButtonContent, cancelButtonLabel: secondaryButtonContent, onSubmit: onSubmitHandleModal, onCancel: handleHideModal, isSubmitButtonDisabled: isSubmitButtonDisabled }) }, modalContent)) : (React.createElement(MobileDropdownSlider, { title: labels?.partsAccessoriesMerch_requiredPartVariantPopUp_header, isActive: isMobileModalActive, handleActiveMobileSlider: closeMobileModal, isRightToLeft: isRightToLeft, footerComponent: React.createElement(ModalWindowFooter, { isCancelButton: Boolean(secondaryButtonContent), isSubmitButton: true, submitButtonLabel: primaryButtonContent, cancelButtonLabel: secondaryButtonContent, onSubmit: onSubmitHandleModal, onCancel: closeMobileModal, isSubmitButtonFullWidth: true, isSubmitButtonDisabled: isSubmitButtonDisabled }) }, modalContent));
}
